import React, { FC, useCallback, useMemo, useState } from 'react';

import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

import { GROUPBY_BUNDLES, GROUPBY_PRODUCTS } from '../../store/constants';
import { DropDownMenu } from './DropDownMenu';
import { replaceURLGroupByEntry } from './group-by-utils';
import { useTranslation } from 'react-i18next';

const GROUP_BY_PRODUCTS = { key: GROUPBY_PRODUCTS, label: 'Products' };
const GROUP_BY_BUNDLES = { key: GROUPBY_BUNDLES, label: 'Curated Collections' };

const DEFAULT_GROUPS = [GROUP_BY_PRODUCTS, GROUP_BY_BUNDLES];

const NO_ADDITIONAL_GROUPS = [];

type Props = {
    // TODO: `current` should be just a string (no need to pass the label  here)
    readonly current?: { readonly key: string; readonly label?: string };
    readonly groups?: ReadonlyArray<{ readonly key: string; readonly label: string }>;
    // TODO: `customGroups` should be removed, it is implicit defined by `groups` being present or not
    readonly customGroups?: boolean;
    readonly className?: string;
    readonly iconCode?: string;
    // TODO: `onGroupBy` should receive just a string (no need to pass the label  here)
    readonly onGroupBy?: (groupBy: { readonly key: string; readonly label: string }) => void;
};

const GroupByDropDown: FC<Props> = ({
    current = GROUP_BY_PRODUCTS,
    groups: additionalGroups = NO_ADDITIONAL_GROUPS,
    customGroups = false,
    className,
    onGroupBy,
    ...props
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const groupOptions = useMemo(
        () => (customGroups ? additionalGroups : DEFAULT_GROUPS.concat(additionalGroups)),
        [customGroups, additionalGroups]
    );

    const handleGroupBy = useCallback(
        (groupBy: { readonly key: string; readonly label: string }) => {
            if (!isEqual(groupBy, current)) {
                // FIXME: manipulating the URL here violates separation of concerns!
                //  The concern of this component should be displaying a dropdown.
                replaceURLGroupByEntry(groupBy);
                onGroupBy?.(groupBy);
                setExpanded(false);
            }
        },
        [onGroupBy, current]
    );

    const applyDefaultFloating = !(className && className.includes('fl-'));

    const currentOption = find(groupOptions, { key: current.key }) ?? groupOptions[0];
    const mainClasses = classNames(className, 'dd-group-by', 'dd-a-r', applyDefaultFloating && 'fl-right');

    return (
        <DropDownMenu
            expanded={expanded}
            onToggleClick={setExpanded}
            onClickOutside={setExpanded}
            align={applyDefaultFloating ? 'right' : 'left'}
            className={mainClasses}
            btnInnerClassName="bt-inner"
            arrowClassName="up arrow fl-right"
            label={t(`components.groupByDropdown.${currentOption.key}`, { defaultValue: currentOption.label })}
            iconCode={props.iconCode}
            iconSizeClass="md-upper"
        >
            {groupOptions.map(groupBy => {
                const isActive = groupBy.key === currentOption.key;
                const check = isActive ? <i className="material-icons">check</i> : null;
                const highlightClass = isActive ? 'checked' : undefined;
                return (
                    <li key={groupBy.key}>
                        <a className={highlightClass} onClick={() => handleGroupBy(groupBy)}>
                            {t(`components.groupByDropdown.${groupBy.key}`, { defaultValue: groupBy.label })}
                            {check}
                        </a>
                    </li>
                );
            })}
        </DropDownMenu>
    );
};

export default GroupByDropDown;
